import { useEffect, useRef, useState } from 'react';
import {
  CandlestickSeries,
  ColorType,
  createChart,
  createSeriesMarkers,
  HistogramSeries,
  LineSeries,
  type IChartApi,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import { resolveIndicators, type IndicatorMarker } from './indicators';
import type { Candle, CandleResponse, IndicatorSelection } from './types';

interface Props {
  data: CandleResponse | null;
  indicators: IndicatorSelection[];
  aux: Partial<Record<string, Candle[]>>;
}
const timestamp = (iso: string): UTCTimestamp => Math.floor(new Date(iso).getTime() / 1000) as UTCTimestamp;

const toSeriesMarker = (marker: IndicatorMarker): SeriesMarker<Time> =>
  marker.kind === 'enter'
    ? { time: timestamp(marker.time), position: 'belowBar', shape: 'arrowUp', color: '#22ab94', text: marker.label }
    : { time: timestamp(marker.time), position: 'aboveBar', shape: 'arrowDown', color: '#f23645', text: marker.label };

export function MarketChart({ data, indicators, aux }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (!container.current) return;
    const instance = createChart(container.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: '#0b0e14' },
        textColor: '#9aa4b5',
        panes: { separatorColor: '#242a35', separatorHoverColor: '#3f4b5f', enableResize: true },
      },
      grid: { vertLines: { color: '#171c25' }, horzLines: { color: '#171c25' } },
      crosshair: {
        vertLine: { color: '#667085', labelBackgroundColor: '#374151' },
        horzLine: { color: '#667085', labelBackgroundColor: '#374151' },
      },
      rightPriceScale: { borderColor: '#242a35' },
      timeScale: { borderColor: '#242a35', timeVisible: true, secondsVisible: false },
      localization: { locale: 'en-US' },
    });
    chart.current = instance;
    return () => {
      instance.remove();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = chart.current;
    if (!instance || !data) return;
    for (const pane of [...instance.panes()].reverse()) {
      for (const series of pane.getSeries()) instance.removeSeries(series);
    }
    const candleSeries = instance.addSeries(CandlestickSeries, {
      upColor: '#22ab94',
      downColor: '#f23645',
      borderVisible: false,
      wickUpColor: '#22ab94',
      wickDownColor: '#f23645',
    });
    candleSeries.setData(
      data.candles.map((candle) => ({
        time: timestamp(candle.time),
        open: Number(candle.open),
        high: Number(candle.high),
        low: Number(candle.low),
        close: Number(candle.close),
      })),
    );

    const volume = instance.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'volume' }, 1);
    volume.setData(
      data.candles
        .filter((c) => c.volume != null)
        .map((c) => ({
          time: timestamp(c.time),
          value: Number(c.volume),
          color: Number(c.close) >= Number(c.open) ? '#22ab9466' : '#f2364566',
        })),
    );
    instance.panes()[1]?.setHeight(100);

    let paneIndex = 2;
    const markers: IndicatorMarker[] = [];
    const nextWarnings: string[] = [];
    for (const { definition, selection } of resolveIndicators(indicators)) {
      const auxSeries = Object.fromEntries(
        Object.entries(aux)
          .filter(([key]) => key.startsWith(`${definition.id}:`))
          .map(([key, candles]) => [key.slice(definition.id.length + 1), candles]),
      );
      const output = definition.calculate(data.candles, selection.params, {
        interval: data.interval,
        assetClass: data.instrument.asset_class,
        timezone: data.instrument.session_timezone,
        auxSeries,
      });
      const targetPane = definition.placement === 'overlay' ? 0 : paneIndex++;
      for (const line of output.lines) {
        const series = instance.addSeries(
          LineSeries,
          { color: line.color, lineWidth: line.width, title: line.label },
          targetPane,
        );
        series.setData(line.points.map((point) => ({ time: timestamp(point.time), value: point.value })));
      }
      if (targetPane > 1) instance.panes()[targetPane]?.setHeight(output.lines.length > 1 ? 140 : 100);
      markers.push(...output.markers);
      nextWarnings.push(...output.warnings.map((warning) => `${definition.label}: ${warning}`));
    }
    if (markers.length) {
      createSeriesMarkers(
        candleSeries,
        markers
          .slice()
          .sort((left, right) => Date.parse(left.time) - Date.parse(right.time))
          .map(toSeriesMarker),
      );
    }
    setWarnings(nextWarnings);
    instance.timeScale().fitContent();
  }, [data, indicators, aux]);

  return (
    <div className="chart-host" aria-label="Candlestick chart">
      <div className="chart-canvas" ref={container} />
      {warnings.length > 0 && (
        <div className="chart-warnings">
          {warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </div>
      )}
    </div>
  );
}
